import { useEffect, useState } from "react";
import { UserCheck } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { TemplateEditor } from "@/pages/ChecklistTemplates";

/**
 * Super-Admin editor for the GLOBAL Visitor Registration checklist.
 * Reuses TemplateEditor from ChecklistTemplates so behaviour, styling and
 * validation match the Daily Tracking editor pixel-for-pixel. The extra
 * `showRequiresReason` flag exposes the per-item "requires reason on Yes"
 * toggle that only Visitor Registration cares about.
 */
export default function AdminVisitorChecklist() {
  const [tpl, setTpl] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/checklist-templates");
      const found = (data.templates || []).find((t) => t.code === "VISITOR_REG");
      if (!found) {
        setErr("Visitor Registration template not seeded. Contact platform support.");
      } else {
        setTpl(found);
      }
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="vb-fade-in" data-testid="admin-visitor-checklist-page">
      <div className="flex items-center gap-3 mb-2">
        <UserCheck size={26} style={{ color: "var(--vb-accent)" }} />
        <h1 className="vb-heading text-3xl" style={{ color: "var(--vb-text-2)" }}>Visitor Registration Form</h1>
      </div>
      <p className="opacity-70 mb-6 max-w-3xl">
        Manage the global visitor health-screening checklist. Add or rename items (English + 中文),
        toggle <strong>Requires reason on Yes</strong> for questions that need the visitor to explain
        a positive answer, and deactivate items you no longer want on new forms. Deactivating never
        deletes history — past visitor entries keep the exact wording they were captured against.
      </p>

      {err && <div className="vb-pill err mb-4" data-testid="admin-vr-error">{err}</div>}

      {loading ? (
        <div className="opacity-60" data-testid="admin-vr-loading">Loading…</div>
      ) : tpl ? (
        <TemplateEditor template={tpl} onSaved={load} showRequiresReason />
      ) : null}
    </div>
  );
}
